import { TranslocoService } from '@jsverse/transloco';
import { GeolocateControl, Map } from 'maplibre-gl';

import { AbstractMapControl } from '@connect-services/client/components/map/abstract-map-control';

export enum GeolocateAvailabilityStateEnum {
    AVAILABLE = 'available',
    REFUSED = 'refused',
}

const GEOLOCATE_EVENT_TO_STATE = [
    ['error', GeolocateAvailabilityStateEnum.REFUSED],
    ['geolocate', GeolocateAvailabilityStateEnum.AVAILABLE],
    ['trackuserlocationend', GeolocateAvailabilityStateEnum.REFUSED],
    ['trackuserlocationstart', GeolocateAvailabilityStateEnum.AVAILABLE],
] as const;

export class GeolocationControl extends AbstractMapControl {
    private container: HTMLElement | null = null;
    private geolocateControl: GeolocateControl | null = null;
    private observer: MutationObserver | null = null;
    private state: GeolocateAvailabilityStateEnum = GeolocateAvailabilityStateEnum.AVAILABLE;

    public constructor(private readonly translocoService: TranslocoService) {
        super();
    }

    public override onAdd(map: Map): HTMLElement {
        this.map = map;

        if (!navigator.geolocation) {
            this.setState(GeolocateAvailabilityStateEnum.REFUSED);

            const fallbackContainer = document.createElement('div');
            fallbackContainer.classList.add('maplibregl-ctrl');
            fallbackContainer.classList.add('maplibregl-ctrl-group');

            return fallbackContainer;
        }

        this.geolocateControl = new GeolocateControl({
            positionOptions: {
                enableHighAccuracy: true,
            },
            trackUserLocation: true,
        });

        this.container = this.geolocateControl.onAdd(map);

        this.setupEvents();

        requestAnimationFrame(() => {
            this.observeButtonMountAndKeepTooltipSynced();
        });

        void this.checkInitialPermissionState();

        return this.container;
    }

    public override onRemove(): void {
        this.disconnectObserver();

        if (this.geolocateControl) {
            this.geolocateControl.onRemove();
        }

        this.geolocateControl = null;
        this.container = null;
        this.map = undefined;
    }

    public refreshTooltip(): void {
        const button = this.getGeolocateButtonElement();
        if (!button) {
            return;
        }

        button.title = this.translocoService.translate(this.getTooltipKey());
    }

    protected initButton(): void {}

    private applyUIState(): void {
        const button = this.getGeolocateButtonElement();
        if (!button) {
            return;
        }

        const container: HTMLElement | null = button.closest('.maplibregl-ctrl-group');
        if (!container) {
            return;
        }

        this.nativeButtonsStyle(container);

        button.classList.toggle('geolocate-refused', this.state === GeolocateAvailabilityStateEnum.REFUSED);
    }

    private async checkInitialPermissionState(): Promise<void> {
        if (!navigator.permissions?.query) {
            return;
        }

        try {
            const permissionStatus = await navigator.permissions.query({
                name: 'geolocation' as PermissionName,
            });

            if (permissionStatus.state === 'denied') {
                this.setState(GeolocateAvailabilityStateEnum.REFUSED);
            }

            permissionStatus.onchange = (): void => {
                if (permissionStatus.state === 'denied') {
                    this.setState(GeolocateAvailabilityStateEnum.REFUSED);

                    return;
                }
                this.setState(GeolocateAvailabilityStateEnum.AVAILABLE);
            };
        } catch {
            /* empty */
        }
    }

    private disconnectObserver(): void {
        this.observer?.disconnect();
        this.observer = null;
    }

    private getGeolocateButtonElement(): HTMLButtonElement | null {
        if (!this.container) {
            return null;
        }

        return this.container.querySelector('.maplibregl-ctrl-geolocate') as HTMLButtonElement | null;
    }

    private getTooltipKey(): string {
        return this.state === GeolocateAvailabilityStateEnum.AVAILABLE
            ? 'mapControl.geolocateFindMyLocation'
            : 'mapControl.geolocateLocationNotAvailable';
    }

    private observeButtonMountAndKeepTooltipSynced(): void {
        const mapContainer = this.map?.getContainer();
        if (!mapContainer) {
            return;
        }

        this.disconnectObserver();

        this.observer = new MutationObserver(() => {
            const button: HTMLButtonElement | null = this.getGeolocateButtonElement();
            if (!button) {
                return;
            }

            this.applyUIState();

            const expectedTitle = this.translocoService.translate(this.getTooltipKey());
            if (button.title === expectedTitle) {
                return;
            }
            button.title = expectedTitle;
        });

        this.observer.observe(mapContainer, {
            attributeFilter: ['title'],
            attributes: true,
            childList: true,
            subtree: true,
        });

        if (!this.getGeolocateButtonElement()) {
            return;
        }

        this.applyUIState();
        this.refreshTooltip();
    }

    private setState(state: GeolocateAvailabilityStateEnum): void {
        this.state = state;

        requestAnimationFrame(() => {
            this.applyUIState();
            this.refreshTooltip();
        });
    }

    private setupEvents(): void {
        if (!this.geolocateControl) {
            return;
        }

        for (const [eventName, state] of GEOLOCATE_EVENT_TO_STATE) {
            this.geolocateControl.on(eventName, () => {
                this.setState(state);
            });
        }
    }
}
